import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BodyRegular, BodyVerySmall } from "../../../atoms/fonts/Body";
import { Column } from "../../../atoms/StructuralLayout";
import { Moment } from "moment";
import { EditHotDeskingButton } from "./EditHotDeskingButton";
import { MovementImage } from "../../../atoms/MovementImage";
import { FavouriteDesk, FavouriteOfficeEntity, OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import {
  selectActivePeriod,
  selectUserRequiredParking,
  updateMovement
} from '@features/whereabouts/store/editMovements.duck';
import { warningNotification } from '@shared/store/notification.duck';
import {
  selectAllOfficeEntities,
  selectFavouriteDesks,
  selectFavouriteOffices,
  selectOffices,
  selectSelectedDayNote,
  setSelectedDayNote
} from '@features/desks/store/advanceHotDeskingSetup.duck';
import { openLocationDialog } from '@features/desks/store/deskReservation.duck';
import { selectCurrentUser, selectIconPack } from '@shared/store/auth.duck';
import {
  doesRootOfficeHaveCarParkingPlan,
  getFullOfficeHierarchy,
  getOfficeChildren
} from '@shared/utils/OfficeHelper';
import { FreeBusyStatus, OFFICE_OPTION, WhereaboutsOption } from '@features/whereabouts/services/WhereaboutOptions';
import { CapacityResponse, getOfficeCapacity } from '@features/desks/services/OfficeCapacityService';
import { Period } from '@features/whereabouts/models/movements.models';
import { FavouritedDeskActivity } from "./FavouritedDeskActivity";
import { WhereaboutsCapacityLabel } from "./WhereaboutsCapacityLabel";
import { selectIsCarParkingEnabled } from '@shared/store/config.duck';
import { iconPackPath } from '@shared/utils/WhereaboutsHelper';
import { useTranslation } from "react-i18next";
import { selectAllWhereaboutsOptions } from '@features/whereabouts/store/whereaboutsOptions.duck';
import { DialogIdentifiers, openDialogWithPayload } from '@shared/store/dialog.duck';
import { addNewNote } from '@features/whereabouts/store/dayNotes.duck';
import { RecentNotes } from "./RecentNotes";
import { DayNotesListContainer } from "./DayNotesActivityItem";
import { AddNoteButton } from "./AddNoteButton";
import styled from "styled-components";
import { ButtonWrapper } from "./ButtonWrapper";

interface Props {
  activeDate: Moment;
}

export default function ActivityAdvanceHotDeskingOffice(props: Props) {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const allOffices = useSelector(selectAllOfficeEntities);
  const offices = useSelector(selectOffices);
  const favoriteOffices = useSelector(selectFavouriteOffices);
  const favoriteDesks = useSelector(selectFavouriteDesks);
  const defaultOffice = useMemo(() => allOffices.find((office: OfficeEntity) => office.id === currentUser?.defaultLocationId), [allOffices, currentUser]);
  const isCarParkingEnabledForCompany = useSelector(selectIsCarParkingEnabled);
  const userRequiresParking = useSelector(selectUserRequiredParking);
  const iconPack = useSelector(selectIconPack);
  const {t} = useTranslation();
  const allWhereaboutsOptions = useSelector(selectAllWhereaboutsOptions);

  const officeOption = useMemo(() => {
    return allWhereaboutsOptions.find(o => o.key === 'OFFICE') ?? OFFICE_OPTION;
  }, [allWhereaboutsOptions]);

  const filteredFavouriteOffices = useMemo(() => {
    return !!defaultOffice ? favoriteOffices.filter(fo => fo.officeId !== defaultOffice.id) : (favoriteOffices || []);
  }, [favoriteOffices, defaultOffice]);

  const favoredOfficeEntities = useMemo(() => {
    if (!!filteredFavouriteOffices) {
      return filteredFavouriteOffices.map((fo: FavouriteOfficeEntity) => allOffices.find((o: OfficeEntity) => o.id === fo.officeId)).filter((office: FavouriteOfficeEntity) => !!office);
    } else {
      return [];
    }
  }, [allOffices, filteredFavouriteOffices]);


  const openEditLocationDialog = useCallback((e: Event, selectedOffice?: OfficeEntity) => {
    e.stopPropagation();
    dispatch(openLocationDialog({selectedOffice: selectedOffice}));
  }, [dispatch]);

  const updateSelectedDay = useCallback((e: Event, whereabouts: WhereaboutsOption, officeEntity?: OfficeEntity, note?: string) => {
    e.stopPropagation();
    if (offices.length > 0) {

      const officeEntityChildren = getOfficeChildren(officeEntity?.id, allOffices);
      if (officeEntity && (officeEntityChildren.length > 0 || officeEntity.deskBookingEnabled)) {
        openEditLocationDialog(e, officeEntity);
      } else if (userRequiresParking && doesRootOfficeHaveCarParkingPlan(allOffices, officeEntity)) {
        openEditLocationDialog(e, officeEntity);
      } else {
        dispatch(updateMovement({selectedOption: whereabouts, locationId: officeEntity?.id ?? 0, dayNote: note}));
      }

    } else {
      warningNotification("You have no offices setup for hot desking.");
      openEditLocationDialog(e);
    }
  }, [allOffices, dispatch, offices.length, openEditLocationDialog, userRequiresParking]);

  const onNoteEnteredOrSelected = useCallback((e: Event, note: string, whereabouts: WhereaboutsOption, officeEntity?: OfficeEntity) => {
    dispatch(setSelectedDayNote(note));
    dispatch(addNewNote({noteToAdd: note, optionId: officeOption.id}));
    updateSelectedDay(e, whereabouts, officeEntity, note)
  }, [dispatch, officeOption.id, updateSelectedDay]);

  const openAddNoteDialog = useCallback((e: any) => {
    e.stopPropagation();
    dispatch(openDialogWithPayload({
      payload: { onSaveClicked: (e: Event, note: string) => onNoteEnteredOrSelected(e, note, officeOption, defaultOffice), whereabouts: officeOption },
      activeDialog: DialogIdentifiers.DayNotesEntryDialog
    }));
  }, [dispatch, officeOption, onNoteEnteredOrSelected, defaultOffice]);

  return <>
    {defaultOffice && <CustomOfficeActivity dataTest="whereabouts-default-office" activeDay={props.activeDate}
                                            office={defaultOffice}
                                            isCarParkingEnabledForCompany={isCarParkingEnabledForCompany}
                                            updateSelectedDay={updateSelectedDay}
                                            officeOption={officeOption}
                                            onNoteEnteredOrSelected={(e, note) => onNoteEnteredOrSelected(e, note, officeOption, defaultOffice)}
                                            openAddNoteDialog={openAddNoteDialog}/>}

    {(favoredOfficeEntities.length === 0 && !defaultOffice) && <>
      <div className="activity" style={{flexDirection: 'column'}} onClick={(e: any) => openEditLocationDialog(e)}>
        <ActivityContainer>
          <MovementImage src={iconPackPath(iconPack, 'Office.svg', FreeBusyStatus.FREE)} alt="At the office"/>
          <BodyRegular weight={600}>{t('whereabouts-labels.OFFICE')}</BodyRegular>
          <ButtonWrapper>
            {officeOption.dayNotesEnabled && <AddNoteButton onClick={openAddNoteDialog} />}
            <EditHotDeskingButton />
          </ButtonWrapper>
        </ActivityContainer>
        {officeOption.dayNotesEnabled && <DayNotesListContainer>
          <RecentNotes whereaboutsOptionId={officeOption.id}
                       onNoteSelected={(e, note) => onNoteEnteredOrSelected(e, note, officeOption, defaultOffice)} />
        </DayNotesListContainer>}
      </div>
    </>}

    {favoriteDesks && favoriteDesks.map((favouriteDesk: FavouriteDesk, key: number) =>
      <FavouritedDeskActivity key={key}
                              openEditLocationDialog={openEditLocationDialog}
                              openAddNoteDialog={openAddNoteDialog}
                              onNoteEnteredOrSelected={(e, note, office) => onNoteEnteredOrSelected(e, note, officeOption, office)}
                              officeOption={officeOption}
                              favouriteDesk={favouriteDesk} />
    )}

    {favoredOfficeEntities && favoredOfficeEntities.map((favouritedOffice: OfficeEntity, key: number) =>
      (<CustomOfficeActivity key={key} activeDay={props.activeDate}
                             office={favouritedOffice}
                             isCarParkingEnabledForCompany={isCarParkingEnabledForCompany}
                             updateSelectedDay={updateSelectedDay}
                             officeOption={officeOption}
                             onNoteEnteredOrSelected={(e, note) => onNoteEnteredOrSelected(e, note, officeOption, favouritedOffice)}
                             openAddNoteDialog={openAddNoteDialog}/>))}
  </>
}

interface CustomOfficeActivityProps {
  dataTest?: string;
  activeDay: Moment;
  office: OfficeEntity;
  isCarParkingEnabledForCompany: boolean;
  updateSelectedDay: (e: Event, whereabouts: WhereaboutsOption, officeEntity?: OfficeEntity, note?: string) => any;
  officeOption: WhereaboutsOption;
  onNoteEnteredOrSelected: (e: any, note: string) => void;
  openAddNoteDialog: (e: any) => void;
}

function CustomOfficeActivity(props: CustomOfficeActivityProps) {
  const {activeDay, office, updateSelectedDay, isCarParkingEnabledForCompany, officeOption, onNoteEnteredOrSelected, openAddNoteDialog} = props;
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [capacity, setOfficeCapacity] = useState<CapacityResponse | undefined>(undefined);

  const allOfficeEntities = useSelector(selectAllOfficeEntities);
  const activePeriod = useSelector(selectActivePeriod);
  const iconPack = useSelector(selectIconPack);
  const selectedDayNote = useSelector(selectSelectedDayNote);

  const officeHierarchy = useMemo(() => getFullOfficeHierarchy(allOfficeEntities, [], office), [allOfficeEntities, office]);
  const officeParentName = useMemo(() => officeHierarchy && officeHierarchy.length > 0 ? officeHierarchy[0].label : '', [officeHierarchy]);
  const officeChildName = useMemo(() => officeHierarchy && officeHierarchy.length > 1 ? officeHierarchy.slice(1).map(o => o.label).join(' - ') : '', [officeHierarchy]);


  const fetchCapacity = useCallback((officeId: number, date: Moment) => {
    if (!isLoading) {
      setIsLoading(true);
      getOfficeCapacity(officeId, date)
        .then((resp) => setOfficeCapacity(resp))
        .finally(() => setIsLoading(false));
    }
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading && !capacity && office.capacity > 0) {
      fetchCapacity(office.id, activeDay);
    }

  }, [isLoading, office, activeDay, capacity, fetchCapacity]);

  useEffect(() => {
    setOfficeCapacity(undefined);
  }, [activeDay])

  const onClick = (e: any) => {
    if (!office.active) {
      dispatch(warningNotification('That space is not available.'));
      return;
    }

    const amUsed = capacity?.amUsed ?? 0;
    const pmUsed = capacity?.pmUsed ?? 0;
    const totalAvailable = capacity?.capacity ?? 0;

    if (isLoading) {
      console.warn('Still loading');
      return;
    }

    if (activePeriod === Period.AM && (office.capacity === 0 || amUsed < totalAvailable)) {
      updateSelectedDay(e, OFFICE_OPTION, office, selectedDayNote);
    }  else if (activePeriod === Period.PM && (office.capacity === 0 || pmUsed < totalAvailable)) {
      updateSelectedDay(e, OFFICE_OPTION, office, selectedDayNote);
    }  else if (activePeriod === Period.AllDay && (office.capacity === 0 || (amUsed < totalAvailable && pmUsed < totalAvailable))) {
      updateSelectedDay(e, OFFICE_OPTION, office, selectedDayNote);
    } else {
      dispatch(warningNotification('That space is at capacity.'));
      console.error('failed to load office capacity')
      if (office.id && activeDay) {
        fetchCapacity(office.id, activeDay);
      }
    }
  }


  let inputAttributes = {};
  if (props.dataTest) {
    // @ts-ignore
    inputAttributes['data-test'] = props.dataTest;
  }

  return <div {...inputAttributes} className={`activity ${(isLoading || !office.active) ? 'activity--disabled' : ''}`.trim()}
              style={{flexDirection: 'column'}}
              onClick={onClick}>
    <ActivityContainer>
      <MovementImage src={iconPackPath(iconPack, 'Office.svg', FreeBusyStatus.FREE)} alt="At the office"/>
      <Column>
        <BodyRegular weight={600}>{officeParentName}</BodyRegular>
        {officeChildName && <BodyVerySmall style={{marginBottom: 8, marginTop: -2}}>{officeChildName}</BodyVerySmall>}
        {office.capacity > 0 && <WhereaboutsCapacityLabel capacity={capacity}
                                                          isCarParkingEnabledForCompany={isCarParkingEnabledForCompany} />}
      </Column>
      <ButtonWrapper>
        {officeOption.dayNotesEnabled && <AddNoteButton onClick={openAddNoteDialog} />}
        <EditHotDeskingButton className="activity__editOfficeButton" office={office}/>
      </ButtonWrapper>
    </ActivityContainer>
    {officeOption.dayNotesEnabled && <DayNotesListContainer>
      <RecentNotes whereaboutsOptionId={officeOption.id}
                   onNoteSelected={onNoteEnteredOrSelected} />
    </DayNotesListContainer>}
  </div>
}

const ActivityContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`
